import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { useSession } from '../stores/session.js';
import { errorMessage } from '../lib/properties.js';
import { listMembers } from '../lib/members-api.js';
import {
  getNotificationSettings,
  saveNotificationSettings,
  NOTIFICATION_SETTINGS_QUERY_KEY,
  type NotificationSettings,
} from '../lib/broker-notifications-api.js';

/**
 * Krok NAP6 bod A14 — Nastavení → Kancelář → „Upozornění".
 *
 * API je hotové od NOTIF1 (`/v1/crm/notification-settings`), chyběla jen
 * obrazovka: kancelář si nastavení nemohla nikde změnit. Karta NIC
 * nepočítá a nic si nepamatuje — zdroj pravdy je odpověď serveru, uloží se
 * PATCHem a stránka si ji přečte znovu.
 *
 * ODKUD SE BERE A KAM TEČE:
 *  * výchozí makléř — seznam kolegů z `/v1/members` (R4), prázdná volba
 *    znamená `null` = vlastník účtu (tak to čte `defaultBrokerUserId`
 *    v `@reos/crm` při zakládání poptávky z webu),
 *  * pracovní doba a souhrn — `crm_notification_settings` téže kanceláře,
 *    podle nich se řídí hodinový souhrn nepřečtených (NOTIF1 bod 4).
 *
 * `listingNoReactionDays` (NAP2 A13) se tu VĚDOMĚ nenastavuje: je to prahu
 * kontroly „inzerát bez reakce" na Dnesku, ne upozornění kanceláře — PATCH
 * ho beze změny nechá být (posílají se jen vyplněná pole).
 *
 * Měnit smí owner/admin — tatáž hranice jako na serveru
 * (`crm.notification_settings.manage`); ostatní vidí hodnoty, ale neuloží.
 *
 * Rozepsané hodnoty jsou PŘEKRYV nad odpovědí serveru (`draft`), ne kopie
 * v `useEffect` — kopie plněná po připojení komponenty je závod (poučení
 * `reos-web-admin-mount-effect-race`).
 */

const WORK_HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export function NotificationSettingsCard(): React.JSX.Element {
  const t = useT();
  const user = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = (user?.roles ?? []).some((r) => r === 'owner' || r === 'admin');

  const settings = useQuery({ queryKey: NOTIFICATION_SETTINGS_QUERY_KEY, queryFn: getNotificationSettings });
  const members = useQuery({ queryKey: ['nastaveni', 'members'], queryFn: listMembers });
  const [draft, setDraft] = React.useState<Partial<NotificationSettings>>({});
  const [note, setNote] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const server = settings.data;
  const view: NotificationSettings | null = server === undefined ? null : { ...server, ...draft };

  async function onSave(): Promise<void> {
    if (view === null) return;
    setNote(null);
    setBusy(true);
    try {
      await saveNotificationSettings({
        defaultBrokerUserId: view.defaultBrokerUserId,
        digestEnabled: view.digestEnabled,
        workHourFrom: view.workHourFrom,
        workHourTo: view.workHourTo,
      });
      setDraft({});
      await queryClient.invalidateQueries({ queryKey: NOTIFICATION_SETTINGS_QUERY_KEY });
      setNote(t('webAdmin.settings.notificationsSaved'));
    } catch (e) {
      setNote(t('webAdmin.settings.notificationsSaveError', { error: errorMessage(e) }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-notifications-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.notificationsTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.notificationsPerex')}</p>

      {view === null ? (
        <p className="text-sm text-gray-600" data-testid="notifications-loading">
          {t('webAdmin.settings.notificationsLoading')}
        </p>
      ) : (
        <>
          <label className="mb-1 block text-sm font-medium" htmlFor="notifications-default-broker">
            {t('webAdmin.settings.notificationsDefaultBrokerLabel')}
          </label>
          <select
            id="notifications-default-broker"
            className="mb-1 w-full rounded border px-2 py-1 text-sm"
            value={view.defaultBrokerUserId ?? ''}
            disabled={!isOwnerOrAdmin}
            data-testid="notifications-default-broker"
            onChange={(e) =>
              setDraft((current) => ({
                ...current,
                defaultBrokerUserId: e.target.value === '' ? null : e.target.value,
              }))
            }
          >
            <option value="">{t('webAdmin.settings.notificationsDefaultBrokerOwner')}</option>
            {(members.data ?? []).map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name.trim() === '' ? member.email : member.name}
              </option>
            ))}
          </select>
          <p className="mb-3 text-xs text-gray-600">{t('webAdmin.settings.notificationsDefaultBrokerHint')}</p>

          <span className="mb-1 block text-sm font-medium">{t('webAdmin.settings.notificationsWorkHoursLabel')}</span>
          <div className="mb-1 flex items-center gap-2">
            <select
              aria-label={t('webAdmin.settings.notificationsWorkHourFrom')}
              className="rounded border px-2 py-1 text-sm"
              value={view.workHourFrom}
              disabled={!isOwnerOrAdmin}
              data-testid="notifications-work-hour-from"
              onChange={(e) => setDraft((current) => ({ ...current, workHourFrom: Number(e.target.value) }))}
            >
              {WORK_HOURS.map((hour) => (
                <option key={hour} value={hour}>
                  {`${hour}:00`}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600">{t('webAdmin.settings.notificationsWorkHoursTo')}</span>
            <select
              aria-label={t('webAdmin.settings.notificationsWorkHourTo')}
              className="rounded border px-2 py-1 text-sm"
              value={view.workHourTo}
              disabled={!isOwnerOrAdmin}
              data-testid="notifications-work-hour-to"
              onChange={(e) => setDraft((current) => ({ ...current, workHourTo: Number(e.target.value) }))}
            >
              {WORK_HOURS.map((hour) => hour + 1).map((hour) => (
                <option key={hour} value={hour}>
                  {`${hour}:00`}
                </option>
              ))}
            </select>
          </div>
          <p className="mb-3 text-xs text-gray-600">{t('webAdmin.settings.notificationsWorkHoursHint')}</p>

          <label className="mb-3 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={view.digestEnabled}
              disabled={!isOwnerOrAdmin}
              data-testid="notifications-digest-enabled"
              onChange={(e) => setDraft((current) => ({ ...current, digestEnabled: e.target.checked }))}
            />
            <span>{t('webAdmin.settings.notificationsDigestLabel')}</span>
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rh-a2-link text-sm"
              disabled={!isOwnerOrAdmin || busy}
              data-testid="notifications-save"
              onClick={() => void onSave()}
            >
              {t('webAdmin.settings.notificationsSaveButton')}
            </button>
            {note !== null ? (
              <span className="text-xs text-gray-600" data-testid="notifications-note">
                {note}
              </span>
            ) : null}
          </div>
          {!isOwnerOrAdmin ? (
            <p className="mt-2 text-xs text-gray-600" data-testid="notifications-readonly">
              {t('webAdmin.settings.notificationsReadonly')}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
